import React, { useState, useEffect, useCallback } from 'react';
import { Table, AutoComplete, Input, Button, message, Tag, Modal, Form, Select } from 'antd';
import { useNavigate } from 'react-router-dom';
import { Api_VariavelGlobal } from '../global';

const { Option } = Select;

const Clientes_Todos = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [options, setOptions] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [clienteSelecionado, setClienteSelecionado] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [form] = Form.useForm();
  const [usuarios, setUsuarios] = useState([]);

  const navigate = useNavigate();

  const formatarData = (dataISO) => {
    if (!dataISO) return '-';
    const data = new Date(dataISO);
    const dia = String(data.getDate()).padStart(2, '0');
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const ano = data.getFullYear();
    return `${dia}/${mes}/${ano}`;
  };
  
  const fetchUsuarios = useCallback(async () => {
  try {
    const response = await fetch(`${Api_VariavelGlobal}/api/users`);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Erro ao buscar usuários: ${response.status} - ${errorText}`);
    }
    const usuarios = await response.json();
    setUsuarios(usuarios);
  } catch (error) {
    console.error('Erro ao buscar os usuários:', error);
    message.error('Erro ao buscar os usuários.');
  }
}, []);


  // Define fetchClientes com useCallback para estabilidade
  const fetchClientes = useCallback(async () => {
    try {
      const response = await fetch(`${Api_VariavelGlobal}/api/clientes`);
      if (!response.ok) throw new Error('Erro ao buscar clientes');
      const clientes = await response.json();

      const clientesFormatados = clientes.map((cliente) => ({
        key: cliente.ID_CLIENTE,
        usuarioId: cliente.ID_USUARIO,
        tipo: cliente.TIPO_CLIENTE,
        razaoSocial: cliente.RAZAO_SOCIAL,
        nomeFantasia: cliente.NOME_FANTASIA,
        responsavel: cliente.RESPONSAVEL,
        cnpj_cpf: cliente.CNPJ_CPF,
        inscricao_estadual: cliente.INSCRICAO_ESTADUAL,
        email: cliente.EMAIL,
        telefone: cliente.TELEFONE || '-',
        cep: cliente.CEP,
        endereco: cliente.ENDERECO,
        numero: cliente.NUMERO,
        complemento: cliente.COMPLEMENTO,
        bairro: cliente.BAIRRO,
        cidade: cliente.CIDADE,
        estado: cliente.ESTADO,
        status: cliente.STATUS,
        dataCadastro: formatarData(cliente.DATA_CADASTRO),
      }));

      setData(clientesFormatados);
      setFilteredData(clientesFormatados);
    } catch (error) {
      console.error('Erro ao buscar os clientes:', error);
      message.error('Erro ao buscar os clientes.');
    }
  }, []);

 useEffect(() => {
  fetchClientes();
  fetchUsuarios();
}, [fetchClientes, fetchUsuarios]);

  const handleSearch = (value) => {
    setSearchValue(value);
    const filtered = data.filter(
      (cliente) =>
        cliente.razaoSocial.toLowerCase().includes(value.toLowerCase()) ||
        cliente.responsavel.toLowerCase().includes(value.toLowerCase()) ||
        cliente.telefone.toLowerCase().includes(value.toLowerCase()) ||
        cliente.cidade.toLowerCase().includes(value.toLowerCase()) ||
        cliente.estado.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredData(filtered);
    setOptions(
      filtered.map((cliente) => ({
        value: cliente.razaoSocial,
        label: `${cliente.razaoSocial} - ${cliente.responsavel}`,
      }))
    );
  };

  const onSelect = (value) => {
    setSearchValue(value);
    const filtered = data.filter((cliente) => cliente.razaoSocial === value);
    setFilteredData(filtered);
  };

  const showEditModal = (cliente) => {
    setClienteSelecionado(cliente);
    form.setFieldsValue({
      usuarioId: cliente.usuarioId,
      tipo: cliente.tipo,
      razaoSocial: cliente.razaoSocial,
      nomeFantasia: cliente.nomeFantasia,
      responsavel: cliente.responsavel,
      cnpj_cpf: cliente.cnpj_cpf,
      inscricao_estadual: cliente.inscricao_estadual,
      email: cliente.email,
      telefone: cliente.telefone,
      cep: cliente.cep,
      endereco: cliente.endereco,
      numero: cliente.numero,
      complemento: cliente.complemento,
      bairro: cliente.bairro,
      cidade: cliente.cidade,
      estado: cliente.estado,
      status: cliente.status,
    });
    setIsModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      setConfirmLoading(true);

      const body = {
        tipoCliente: values.tipo,
        razaoSocial: values.razaoSocial,
        nomeFantasia: values.nomeFantasia,
        responsavel: values.responsavel,
        cnpjCpf: values.cnpj_cpf,
        inscricaoEstadual: values.inscricao_estadual,
        email: values.email,
        telefone: values.telefone,
        cep: values.cep,
        endereco: values.endereco,
        numero: values.numero,
        complemento: values.complemento,
        bairro: values.bairro,
        cidade: values.cidade,
        estado: values.estado,
        status: values.status,
        ID_USUARIO: values.usuarioId,
      };

      const response = await fetch(`${Api_VariavelGlobal}/api/clientes/${clienteSelecionado.key}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        message.success('Cliente atualizado com sucesso!');
        setIsModalVisible(false);
        fetchClientes();
      } else {
        message.error('Erro ao atualizar cliente.');
      }
    } catch (error) {
      console.error('Erro ao atualizar cliente:', error);
      message.error('Erro ao atualizar cliente.');
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const columns = [
    {
      title: 'Tipo',
      dataIndex: 'tipo',
      key: 'tipo',
      width: 120,
      render: (tipo) => (tipo === 'PJ' ? 'Pessoa Jurídica' : 'Pessoa Física'),
    },
    {
      title: 'Razão Social',
      dataIndex: 'razaoSocial',
      key: 'razaoSocial',
    },
    {
      title: 'Nome Fantasia',
      dataIndex: 'nomeFantasia',
      key: 'nomeFantasia',
    },
    {
      title: 'Responsável',
      dataIndex: 'responsavel',
      key: 'responsavel',
    },
    {
      title: 'Telefone',
      dataIndex: 'telefone',
      key: 'telefone',
    },
    {
      title: 'Cidade',
      dataIndex: 'cidade',
      key: 'cidade',
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      width: 80,
      render: (estado) => <Tag color="blue">{estado}</Tag>,
    },
    {
      title: 'Número',
      dataIndex: 'numero',
      key: 'numero',
      width: 80,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status) =>
        status === 1 ? <Tag color="green">Ativo</Tag> : <Tag color="red">Inativo</Tag>,
    },
    {
      title: 'Data Cadastro',
      dataIndex: 'dataCadastro',
      key: 'dataCadastro',
      width: 120,
    },
    {
      title: 'Ação',
      key: 'acao',
      render: (_, cliente) => (
        <Button type="link" onClick={() => showEditModal(cliente)}>
          Editar
        </Button>
      ),
    },
  ];
  
  const getNomeUsuario = (id) => {
  const usuario = usuarios.find((u) => u.ID_USUARIO === id);
  return usuario ? usuario.LOGIN : ''; // ou .EMAIL, se preferir exibir o email
};

  return (
    <div style={{ background: '#fff', minHeight: '100vh' }}>
      <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
        <AutoComplete
          style={{ width: 300 }}
          value={searchValue}
          options={options}
          onSelect={onSelect}
          onSearch={handleSearch}
          placeholder="Buscar clientes"
          allowClear
        >
          <Input.Search size="middle" />
        </AutoComplete>

        <Button type="primary" onClick={() => navigate('/Supplier_Form')}>
          Cadastrar Cliente
        </Button>
      </div>

      <Table columns={columns} dataSource={filteredData} rowKey="key" />

      <Modal
       title={
  <span>
    Editar Cliente{' '}
    {clienteSelecionado?.usuarioId && (
      <Tag color="blue">Vendedor: {getNomeUsuario(clienteSelecionado.usuarioId)}</Tag>
    )}
  </span>
}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        confirmLoading={confirmLoading}
        width={700}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="tipo"
            label="Tipo"
            rules={[{ required: true, message: 'Informe o tipo' }]}
          >
            <Select placeholder="Selecione o tipo">
              <Option value="PJ">Pessoa Jurdica</Option>
              <Option value="PF">Pessoa Física</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="razaoSocial"
            label="Razão Social"
            rules={[{ required: true, message: 'Informe a razo social' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="nomeFantasia"
            label="Nome Fantasia"
            rules={[{ required: true, message: 'Informe o nome fantasia' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="responsavel"
            label="Responsável"
            rules={[{ required: true, message: 'Informe o responsável' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="cnpj_cpf"
            label="CNPJ ou CPF"
            rules={[{ required: true, message: 'Informe o CNPJ ou CPF' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="inscricao_estadual" label="Inscrio Estadual">
            <Input />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[{ type: 'email', message: 'Email inválido' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="telefone" label="Telefone">
            <Input />
          </Form.Item>

          <Form.Item
            name="cep"
            label="CEP"
            rules={[{ required: true, message: 'Informe o CEP' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="endereco"
            label="Endereço"
            rules={[{ required: true, message: 'Informe o endereço' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="numero"
            label="Nmero"
            rules={[{ required: true, message: 'Informe o número' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="complemento" label="Complemento">
            <Input />
          </Form.Item>

          <Form.Item
            name="bairro"
            label="Bairro"
            rules={[{ required: true, message: 'Informe o bairro' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="cidade"
            label="Cidade"
            rules={[{ required: true, message: 'Informe a cidade' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="estado"
            label="Estado"
            rules={[{ required: true, message: 'Informe o estado' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="status"
            label="Status"
            rules={[{ required: true, message: 'Informe o status' }]}
          >
            <Select placeholder="Selecione o status">
              <Option value={1}>Ativo</Option>
              <Option value={0}>Inativo</Option>
            </Select>
          </Form.Item>
          <Form.Item
              name="usuarioId"
              label="Vendedor responsável"
              rules={[{ required: true, message: 'Selecione o usuário' }]}
            >
              <Select placeholder="Selecione o vendedor">
                {usuarios.map((usuario) => (
                  <Option key={usuario.ID_USUARIO} value={usuario.ID_USUARIO}>
                    {usuario.LOGIN || usuario.EMAIL}
                  </Option>
                ))}
              </Select>
            </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Clientes_Todos;
